import { inject, Injectable } from '@angular/core';
import { SettingsService } from '@core/settings/settings.service';
import { SeoService } from '@targx/services/seo/seo.service';
import { environment } from 'environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AutomobiliaSeoService extends SeoService {
  private settingsService = inject(SettingsService);

  setMarketplace(marketplaceSeo: any): void {
    this.emptyJsonSnippet();

    const marketplacePage = marketplaceSeo.page?.data?.attributes;
    const marketplacePageSeo = marketplaceSeo.page?.data?.attributes?.SEO;

    if (!marketplacePageSeo) {
      const seoSettings = this.settingsService.settings?.data?.attributes.SEO;
      this.handleSeoFromApi(seoSettings);

      this.setTitle(marketplacePage.name + ' - ' + seoSettings.metaTitle);

      if (marketplacePage?.metaImage) {
        this.setImage(
          `${environment.assets}${marketplacePage?.Banner[0]?.images?.data[0]?.attributes?.url}`,
        );
      }

      this.setUrl('automobilia');
      return;
    }

    this.handleSeoFromApi(marketplacePageSeo);
    this.setUrl('automobilia');
  }

  setProduct(product: any): void {
    this.emptyJsonSnippet();

    const seoItem = product?.data?.attributes.SEO;
    const productItem = product?.data?.attributes;
    const seoSettings = this.settingsService.settings?.data?.attributes.SEO;

    const defaultImage = seoSettings?.metaImage?.data?.attributes;

    const productUrl =
      this.defaultUrl +
      `automobilia/${product?.data?.attributes.slug}-p${product?.data?.id}`;

    if (!seoItem) {
      this.handleSeoFromApi(seoSettings);

      if (seoSettings?.metaImage) {
        this.setImage(
          `${environment.assets}${seoSettings?.metaImage?.data?.attributes?.url}`,
        );
      }
    }

    this.handleSeoFromApi(seoItem);
    this.setUrl(
      `automobilia/${product?.data?.attributes.slug}-p${product?.data?.id}`,
    );

    this.setTag({
      property: 'og:type',
      content: 'Product',
    });

    this.setTag({
      property: 'og:locale',
      content: productItem.locale,
    });

    this.setTag({
      property: 'article:modified_time',
      content: productItem.updatedAt,
    });
    this.setTag({
      property: 'article:published_time',
      content: productItem.publishedAt,
    });

    if (!seoItem?.metaTitle) {
      this.setTitle(productItem.name);
    }

    if (!seoItem?.metaDescription) {
      this.setDescription(productItem?.description || productItem.name);
    }

    if (!seoItem?.metaImage && productItem?.images?.data.length > 0) {
      this.setImage(
        `${environment.assets}${productItem?.images?.data[0]?.attributes.url}`,
      );
    }

    if (!seoItem?.structuredData) {
      // empty first
      this.emptyJsonSnippet();

      let thumbnailUrl = defaultImage.url;
      if (productItem?.images?.data?.length > 0) {
        thumbnailUrl = `${environment.assets}${productItem?.images?.data[0]?.attributes?.formats?.thumbnail?.url}`;
      }

      this.updateJsonSnippet({
        '@type': 'WebPage',
        '@id': productUrl,
        url: productUrl,
        name: productItem?.name + ' - ' + seoSettings?.metaTitle,
        isPartOf: {
          '@id': 'https://motorbest.pt/#website',
        },
        primaryImageOfPage: {
          '@id': productUrl + '/#primaryimage',
        },
        image: {
          '@id': productUrl + '/#primaryimage',
        },
        thumbnailUrl: thumbnailUrl,
        datePublished: productItem.publishedAt,
        dateModified: productItem.updatedAt,
        author: {
          '@id':
            'https://motorbest.pt/#/schema/person/446bbf469e9486315002b1ff03e7b5ac',
        },
        breadcrumb: {
          '@id': productUrl + '/#breadcrumb',
        },
        inLanguage: productItem?.locale,
        potentialAction: [
          {
            '@type': 'ReadAction',
            target: [productUrl],
          },
        ],
      });

      let productImage = defaultImage;
      if (productItem?.images?.data?.length > 0) {
        productImage = productItem?.images?.data[0]?.attributes?.formats?.large;
        if (!productImage) {
          productImage = productItem?.images?.data[0]?.attributes;
        }
      }

      this.updateJsonSnippet({
        '@context': 'https://schema.org',
        '@type': 'Product',
        description: productItem?.description,
        url: productUrl,
        name: productItem?.name,
        image: `${environment.assets}${productImage?.url}`,
        brand: {
          '@type': 'Brand',
          name: productItem?.product_brand?.data?.attributes?.name ?? '',
        },
        offers: {
          '@type': 'AggregateOffer',
          availability: 'https://schema.org/InStock',
          offerCount: 1,
          lowPrice: productItem?.price,
          highPrice: productItem?.price,
          priceCurrency: 'EUR',
        },
      });

      // add a breadcrumb: Turtles > Turtle Rock
      if (productItem?.images?.data.length > 0) {
        this.updateJsonSnippet({
          '@type': 'ImageObject',
          inLanguage: productItem?.locale,
          '@id': productUrl + '/#primaryimage',
          url: `${environment.assets}${productItem?.images?.data[0]?.attributes?.url}`,
          contentUrl: `${environment.assets}${productItem?.images?.data[0]?.attributes?.url}`,
          width: productItem?.images?.data[0]?.attributes?.width,
          height: productItem?.images?.data[0]?.attributes?.height,
        });
      }

      // add a breadcrumb: Turtles > Turtle Rock
      this.updateJsonSnippet({
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Início',
            item: this.defaultUrl,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Marketplace',
            item: this.defaultUrl + `/marketplace`,
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: productItem.name,
          },
        ],
      });

      // add a breadcrumb: Turtles > Turtle Rock
      this.updateJsonSnippet({
        '@type': 'WebSite',
        '@id': 'https://motorbest.pt/#website',
        url: 'https://motorbest.pt/',
        name: seoSettings.metaTitle,
        description: seoSettings.metaDescription,
        potentialAction: [
          {
            '@type': 'SearchAction',
            target: {
              '@type': 'EntryPoint',
              urlTemplate: 'https://motorbest.pt/?s={search_term_string}',
            },
            'query-input': 'required name=search_term_string',
          },
        ],
        inLanguage: productItem?.locale,
      });
    }
  }
}
